import React, { useEffect, useState } from 'react';
import { Image, Modal } from 'react-bootstrap';
import { connect, useDispatch } from 'react-redux';

import { DatePicker } from 'antd';
import moment from 'moment';

import { OnlyDropdown } from 'components/base/ActionsDropdown';
import { RadiosItem } from 'components/base/BaseFilter';
import PromptButton, { BUTTON_TYPES } from 'components/base/prompt/BaseButtons';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import { weekStartSunday } from 'config/CustomEnums';
import { calculateTimeRange } from 'services/MetricTableAPIHelper';

import { TimeFormater } from 'utils/TimeFormatUtil';
import { createAction } from 'utils/index';

import CloseIcon from 'assets/images/prompt_close_icon.svg';

import './AddTableTimeRange.scss';

const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYY-MM-DD';

const TypeConfig = [
  { name: 'Custom', value: 0 },
  { name: 'Last', value: 1 },
  { name: `<x> - to - date`, value: 2 },
  { name: `<x>th week in a year`, value: 3 },
  { name: `Same period for last <x>`, value: 4 },
];

const TimeRangeLastConfig = [
  { name: 'Last day', value: 0 },
  { name: 'Last week', value: 1 },
  { name: 'Last month', value: 2 },
  { name: 'Last quarter', value: 3 },
  { name: 'Last year', value: 4 },
];

const TimeRangePeriodLastConfig = [
  { name: 'day', value: 0 },
  { name: 'week', value: 1 },
  { name: 'month', value: 2 },
  // { name: 'quarter', value: 3 },
  { name: 'year', value: 4 },
];

const TimeRangeYearStart = 2000;

const mapPropsToState = (state) => ({
  metricTableDataLoading: state.metricTable.metricTableDataLoading,
  timeRangeConfigs: state.metricTable.timeRangeConfigs,
});

const AddTableTimeRange = ({
  metricTableDataLoading,
  timeRangeConfigs,
  editTimeRangeIndex,
  onClose = () => {},
}) => {
  const dispatch = useDispatch();
  const show = editTimeRangeIndex >= 0;

  const yearsOptionsConfig = React.useMemo(() => {
    const now = moment();
    let year = now.year();
    const years = [];
    while (year > TimeRangeYearStart) {
      years.push(year);
      year -= 1;
    }
    return years?.map((x) => ({
      name: `${x}`,
      value: Number(x),
    }));
  }, []);

  const timeRangePeriodOptions = React.useMemo(() => {
    const options = timeRangeConfigs
      ?.filter((config, index) => index !== editTimeRangeIndex)
      ?.filter(
        (config) =>
          Number(config?.timeRangeType) >= 0 &&
          Number(config?.timeRangeType) < 4,
      )
      ?.map((config) => ({
        name: config.name,
        value: config.hash,
      }));
    return options;
  }, [timeRangeConfigs, editTimeRangeIndex]);

  const [timeRangeName, setTimeRangeName] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timeRangeType, setTimeRangeType] = useState(0);
  const [timeRangeLastType, setTimeRangeLastType] = useState(null);
  const [timeRangeToDateType, setTimeRangeToDateType] = useState(null);
  const [timeRangeWeekX, setTimeRangeWeekX] = useState(null);
  const [timeRangeWeekYear, setTimeRangeWeekYear] = useState(moment().year());
  const [timeRangePeriodLastType, setTimeRangePeriodLastType] = useState(null);
  const [timeRangePeriodLastHash, setTimeRangePeriodLastHash] = useState(null);

  useEffect(() => {
    if (timeRangeType <= 0) {
      return;
    }
    if (timeRangeType < 4) {
      const { start, end } = calculateTimeRange({
        timeRangeType,
        timeRangeLastType,
        timeRangeToDateType,
        timeRangeWeekX,
        timeRangeWeekYear,
      });
      setStartDate(start);
      setEndDate(end);
      return;
    }
    if (timeRangeType === 4) {
      const originConfig = timeRangeConfigs?.find(
        (config) =>
          config.hash === timeRangePeriodLastHash && config?.timeRangeType < 4,
      );
      console.log(
        '🚀 ~ AddTableTimeRange.js:130 ~ AddTableTimeRange ~ originConfig:',
        originConfig,
      );
      if (originConfig) {
        const originResult = calculateTimeRange(originConfig);
        const timeRangePeriodStartDate = originResult?.start;
        const timeRangePeriodEndDate = originResult?.end;
        const { start, end } = calculateTimeRange({
          timeRangeType,
          timeRangePeriodStartDate,
          timeRangePeriodEndDate,
          timeRangePeriodLastType,
        });
        setStartDate(start);
        setEndDate(end);
        return;
      }
    }
    setStartDate(null);
    setEndDate(null);
  }, [
    timeRangeType,
    timeRangeLastType,
    timeRangeToDateType,
    timeRangeWeekX,
    timeRangeWeekYear,
    timeRangePeriodLastType,
    timeRangePeriodLastHash,
  ]);

  const refreshTimeRangeConfig = () => {
    const config = timeRangeConfigs?.[editTimeRangeIndex];
    setTimeRangeName(config?.name ?? '');
    setTimeRangeType(config?.timeRangeType ?? 0);
    setStartDate(
      config?.timeRangeCustomStart
        ? moment(config?.timeRangeCustomStart)
        : null,
    );
    setEndDate(
      config?.timeRangeCustomEnd ? moment(config?.timeRangeCustomEnd) : null,
    );
    setTimeRangeLastType(config?.timeRangeLastType ?? null);
    setTimeRangeToDateType(config?.timeRangeToDateType ?? null);
    setTimeRangeWeekX(config?.timeRangeWeekX ?? null);
    setTimeRangeWeekYear(config?.timeRangeWeekYear ?? moment().year());
    setTimeRangePeriodLastType(config?.timeRangePeriodLastType ?? null);
    setTimeRangePeriodLastHash(config?.timeRangePeriodLastHash ?? null);
  };

  useEffect(() => {
    if (editTimeRangeIndex < 0) {
      setTimeRangeName(null);
      setTimeRangeType(0);
      setStartDate(null);
      setEndDate(null);
      setTimeRangeLastType(null);
      setTimeRangeToDateType(null);
      setTimeRangeWeekX(null);
      setTimeRangeWeekYear(moment().year());
      setTimeRangePeriodLastType(null);
      setTimeRangePeriodLastHash(null);
      return;
    }
    refreshTimeRangeConfig();
  }, [timeRangeConfigs, editTimeRangeIndex]);

  const confirmEnable = React.useMemo(
    () => moment(endDate).isAfter(moment(startDate)),
    [startDate, endDate],
  );

  const refreshTableData = () => {
    dispatch(createAction('metricTable/getMetricTableData')());
  };

  const saveAction = () => {
    if (metricTableDataLoading) {
      return;
    }
    const newConfig = {
      name:
        timeRangeName && timeRangeName?.length > 0
          ? timeRangeName
          : `Custom time range #${timeRangeConfigs?.length}`,
      timeRangeType,

      timeRangeCustomStart: moment(startDate).format(DATE_FORMAT),
      timeRangeCustomEnd: moment(endDate).format(DATE_FORMAT),
      timeRangeLastType,
      timeRangeToDateType,
      timeRangeWeekX,
      timeRangeWeekYear,
      timeRangePeriodLastType,
      timeRangePeriodLastHash,
    };
    let newConfigs = [];
    if (editTimeRangeIndex < timeRangeConfigs?.length) {
      newConfigs = timeRangeConfigs?.map((config, index) => {
        if (index === editTimeRangeIndex) {
          return {
            ...newConfig,
            hash: config?.hash,
          };
        }
        return config;
      });
    } else {
      newConfigs = timeRangeConfigs.concat([newConfig]);
    }
    dispatch(
      createAction('metricTable/updateMetricTableConfig')({
        timeRangeConfigs: newConfigs,
        successAction: () => refreshTableData(),
      }),
    );
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
    >
      <Modal.Body
        className={`base-prompt-container customer-detail-prompt time-range-modal`}
      >
        <Image
          src={CloseIcon}
          className="base-prompt-close-button customer-detail-prompt-close-button"
          onClick={onClose}
        />

        <label className="customer-detail-modal-title">
          {editTimeRangeIndex < timeRangeConfigs?.length
            ? 'Edit Time Range'
            : 'Add Time Range'}
        </label>

        <div className="d-flex flex-column">
          <CustomTitleLabel title="Time range name (optional)" />
          <input
            className="custom-markdown-area-title input-long-width"
            type="text"
            onChange={({ target }) => {
              setTimeRangeName(target.value);
            }}
            value={timeRangeName}
          />

          <CustomTitleLabel title="Range type" />
          <OnlyDropdown
            customClass="input-long-width"
            options={TypeConfig}
            toggleText={
              TypeConfig?.find((config) => config.value === timeRangeType)?.name
            }
            setValue={(value) => {
              setTimeRangeType(value);
            }}
          />

          <div className="time-range-area">
            <CustomTitleLabel title="Configure time range" />
            <br />
            {/*custom*/}
            <div
              className={
                timeRangeType === 0
                  ? 'base-filter-date-range-picker'
                  : 'hide-field'
              }
            >
              <RangePicker
                value={[
                  startDate ? moment(startDate) : null,
                  endDate ? moment(endDate) : null,
                ]}
                format={TimeFormater.dayMonthYear}
                separator={'-'}
                onChange={(dates, dateStrings) => {
                  if (dates) {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                  } else {
                    setStartDate(null);
                    setEndDate(null);
                  }
                }}
              />
            </div>
            {/*last*/}
            <div
              className={
                timeRangeType === 1
                  ? 'd-inline-flex flex-row flex-gap-24'
                  : 'hide-field'
              }
            >
              {TimeRangeLastConfig?.map(({ name, value }) => (
                <RadiosItem
                  title={name}
                  selected={timeRangeLastType === value}
                  onChange={() => setTimeRangeLastType(value)}
                />
              ))}
            </div>
            {/*x - to - date*/}
            <div
              className={
                timeRangeType === 2
                  ? 'd-inline-flex flex-row flex-gap-24'
                  : 'hide-field'
              }
            >
              <RadiosItem
                title="Week - to - date"
                toolTips={
                  'Start: Current week’s Monday/Sunday 00:00\n' + 'End: now'
                }
                selected={timeRangeToDateType === 0}
                onChange={() => setTimeRangeToDateType(0)}
              />
              <RadiosItem
                title="Month - to - date"
                toolTips={'Start: Current month’s 1st 00:00\n' + 'End: now'}
                selected={timeRangeToDateType === 1}
                onChange={() => setTimeRangeToDateType(1)}
              />
              <RadiosItem
                title="Year - to - date"
                toolTips={'Start: Current year’s Jan 1st 00:00\n' + 'End: now'}
                selected={timeRangeToDateType === 2}
                onChange={() => setTimeRangeToDateType(2)}
              />
            </div>
            {/*xth week in year*/}
            <div
              className={
                timeRangeType === 3
                  ? 'd-flex flex-row flex-gap-16'
                  : 'hide-field'
              }
            >
              <div className="d-flex flex-column">
                <label className="section-caution-description time-range-tip">
                  Week
                </label>
                <input
                  className="custom-markdown-area-title input-width-192"
                  type="number"
                  onChange={({ target }) => {
                    setTimeRangeWeekX(target.value);
                  }}
                  value={timeRangeWeekX}
                />
              </div>
              <div className="d-flex flex-column">
                <label className="section-caution-description time-range-tip">
                  Year
                </label>
                <OnlyDropdown
                  customClass="input-width-192"
                  options={yearsOptionsConfig}
                  toggleText={
                    yearsOptionsConfig?.find(
                      (config) => config.value === timeRangeWeekYear,
                    )?.name
                  }
                  setValue={(value) => setTimeRangeWeekYear(value)}
                />
              </div>
            </div>
            {/*Same period for last <x>*/}
            <div
              className={
                timeRangeType === 4
                  ? 'd-flex flex-row flex-gap-8'
                  : 'hide-field'
              }
            >
              <label className="section-caution-description time-range-field-label">
                Last
              </label>
              <OnlyDropdown
                customClass="input-short-width"
                options={TimeRangePeriodLastConfig}
                toggleText={
                  TimeRangePeriodLastConfig?.find(
                    (x) => x.value === timeRangePeriodLastType,
                  )?.name
                }
                setValue={(value) => setTimeRangePeriodLastType(value)}
              />
              <label className="section-caution-description time-range-field-label">
                of
              </label>
              <OnlyDropdown
                customClass="input-width-time-range-options"
                options={timeRangePeriodOptions}
                toggleText={
                  timeRangePeriodOptions?.find(
                    (x) => x.value === timeRangePeriodLastHash,
                  )?.name
                }
                setValue={(value) => setTimeRangePeriodLastHash(value)}
              />
            </div>
            <label className="section-caution-description time-range-tip">
              {
                'Start time included in time range; End time excluded in time range.'
              }
            </label>
          </div>

          <div>
            <CustomTitleLabel title="Actual time range" />
            <br />
            <label className="section-caution-description">
              {startDate != null && endDate != null
                ? `${moment(startDate).format(
                    DATE_FORMAT,
                  )}T00:00:00 ≤ col < ${moment(endDate).format(
                    DATE_FORMAT,
                  )}T00:00:00`
                : '-'}
            </label>
          </div>
        </div>

        <div className="base-prompt-buttons customer-detail-prompt-buttons">
          <PromptButton
            className="modal-reset-button"
            title="Reset"
            action={() => {
              refreshTimeRangeConfig();
            }}
            type={BUTTON_TYPES.secondary}
          />

          <PromptButton
            title="Cancel"
            action={onClose}
            type={BUTTON_TYPES.secondary}
          />
          <PromptButton
            title={
              editTimeRangeIndex < timeRangeConfigs?.length
                ? 'Confirm to Edit'
                : 'Confirm to Add'
            }
            action={saveAction}
            type={BUTTON_TYPES.main}
            disabled={!confirmEnable}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default connect(mapPropsToState)(AddTableTimeRange);
